/**
 * English translations, keyed by the Italian source string (Italian is the
 * source language). Only strings that actually change need an entry — anything
 * missing falls back to the Italian text, so partial coverage is safe.
 *
 * Add strings as screens get translated. Keep keys byte-for-byte identical to
 * the JSX source (same accents, punctuation, ellipsis "…", placeholders "{x}").
 */
export const EN: Record<string, string> = {
  // ── Shell · navigation / titles / sections ──
  'Tool & programmi': 'Tools & apps',
  'Mail aziendali': 'Company mail',
  'Wiki aziendale': 'Company wiki',
  Formazione: 'Training',
  'Appunti personali': 'Personal notes',
  Turni: 'Shifts',
  Calendario: 'Calendar',
  'Ferie e permessi': 'Leave & time off',
  Consegne: 'Handovers',
  'Consegne di fine giornata': 'End-of-day handovers',
  'Dashboard Admin': 'Admin dashboard',
  'Il mio profilo': 'My profile',
  Impostazioni: 'Settings',
  Task: 'Tasks',
  Corsi: 'Courses',
  // sections
  Operatività: 'Operations',
  Comunicazioni: 'Communications',
  Rubrica: 'Directory',
  Organizzazione: 'Organization',
  Amministrazione: 'Administration',
  // breadcrumbs
  Panoramica: 'Overview',
  Personale: 'Personal',

  // ── Shell · topbar / sidebar / notifications ──
  'Apri menu': 'Open menu',
  'Chiudi menu': 'Close menu',
  'Menu principale': 'Main menu',
  'Navigazione principale': 'Main navigation',
  'Apri tutte le sezioni': 'Open all sections',
  'Apri profilo': 'Open profile',
  'Cerca prenotazioni, attrazioni, infostaff…': 'Search bookings, attractions, infostaff…',
  Notifiche: 'Notifications',
  'Notifiche, {n} non lette': 'Notifications, {n} unread',
  '{n} non lette': '{n} unread',
  'Segna tutte lette': 'Mark all read',
  'Chiudi notifiche': 'Close notifications',
  'Nessuna notifica.': 'No notifications.',

  // ── Common ──
  Oggi: 'Today',
  Annulla: 'Cancel',
  Salva: 'Save',
  Elimina: 'Delete',
  Chiudi: 'Close',
  Tutti: 'All',
  Reparto: 'Department',
  'Segna come letto': 'Mark as read',

  // ── Categories (Infostaff base categories) ──
  Operativo: 'Operations',
  Sicurezza: 'Safety',
  Generale: 'General',

  // ── Infostaff ──
  'Comunicazioni interne al team.': 'Internal team announcements.',
  'Nuovo infostaff': 'New infostaff',
  'Titolo comunicazione': 'Announcement title',
  'Scrivi la comunicazione… (usa la barra per formattare, immagini, video e link)':
    'Write the announcement… (use the toolbar to format, add images, videos and links)',
  'Allega un corso (facoltativo):': 'Attach a course (optional):',
  '— nessun corso —': '— no course —',
  ' · obbligatorio': ' · mandatory',
  'Destinatari:': 'Recipients:',
  'Invia anche via email': 'Also send by email',
  'Segna come urgente': 'Mark as urgent',
  Pubblica: 'Publish',
  'Nessuna comunicazione': 'No announcements',
  'Pubblica la prima con “Nuovo infostaff”.': 'Publish the first one with “New infostaff”.',
  'Le comunicazioni interne pubblicate compaiono qui.': 'Published internal announcements appear here.',
  Urgente: 'Urgent',
  'Corso allegato': 'Attached course',
  'Vai al corso': 'Go to course',

  // ── Leave & time off ──
  Ferie: 'Vacation',
  Permesso: 'Time off',
  Malattia: 'Sick leave',
  'In attesa': 'Pending',
  Approvata: 'Approved',
  Rifiutata: 'Rejected',
  'Invio non riuscito.': 'Submission failed.',
  Tipo: 'Type',
  'Permesso a ore (una giornata)': 'Hourly time off (single day)',
  Giorno: 'Day',
  Dalle: 'From',
  Alle: 'To',
  Dal: 'From',
  Al: 'To',
  'Motivo (opzionale)': 'Reason (optional)',
  'Es. viaggio, visita medica…': 'e.g. trip, medical appointment…',
  'Invia richiesta': 'Submit request',
  ' a ore': ' (hourly)',
  'Approvata da {by}': 'Approved by {by}',
  'Rifiutata da {by}': 'Rejected by {by}',
  Approva: 'Approve',
  Rifiuta: 'Reject',
  'Motivo del rifiuto (opzionale):': 'Reason for rejection (optional):',
  'Ritirare la richiesta?': 'Withdraw this request?',
  Ritira: 'Withdraw',
  'Richieste di assenza con approvazione. Le richieste approvate finiscono in calendario e nei turni.':
    'Absence requests with approval. Approved requests appear in the calendar and on the roster.',
  'Nuova richiesta': 'New request',
  'Da approvare': 'To approve',
  'Le mie richieste': 'My requests',
  'Nessuna richiesta': 'No requests',
  'Invia la tua prima richiesta di ferie o permesso.': 'Submit your first leave or time-off request.',
  'Le richieste del team compaiono qui.': 'Team requests appear here.',
  'In attesa di approvazione': 'Awaiting approval',
  'Nessuna richiesta in attesa.': 'No pending requests.',
  Gestite: 'Handled',

  // ── Hub ──
  Domani: 'Tomorrow',
  Ieri: 'Yesterday',
  alta: 'high',
  media: 'medium',
  bassa: 'low',
  'Segna come da fare': 'Mark as to-do',
  'Segna come fatta': 'Mark as done',
  Aperte: 'Open',
  'Alta priorità': 'High priority',
  Alta: 'High',
  'In ritardo': 'Overdue',
  Completate: 'Completed',
  Fatte: 'Done',
  Tutte: 'All',
  'Task oggi': 'Tasks today',
  'Infostaff da leggere': 'Infostaff to read',
  'tutto in linea': 'all on track',
  'da recuperare': 'to catch up',
  'nessuna nuova': 'nothing new',
  'comunicazioni nuove': 'new announcements',
  Buongiorno: 'Good morning',
  'Buon pomeriggio': 'Good afternoon',
  Buonasera: 'Good evening',
  'Hai un corso obbligatorio da completare': 'You have a mandatory course to complete',
  'Hai {n} corsi obbligatori da completare': 'You have {n} mandatory courses to complete',
  '{n} in ritardo': '{n} overdue',
  scaduto: 'overdue',
  '+{n} altri': '+{n} more',
  'Vai alla formazione': 'Go to training',
  'Le tue task': 'Your tasks',
  'Invio per aggiungere · clicca per aprire': 'Enter to add · click to open',
  'Aggiungi una task… (Invio per salvare)': 'Add a task… (Enter to save)',
  Aggiungi: 'Add',
  'Nessuna task completata': 'No completed tasks',
  'Nessuna task in ritardo, ottimo!': 'No overdue tasks, great!',
  'Nessuna task ad alta priorità': 'No high-priority tasks',
  'Nessuna task — aggiungine una qui sopra per iniziare.': 'No tasks — add one above to get started.',
  'Prossimi appuntamenti': 'Upcoming events',
  'Calendario →': 'Calendar →',
  'Nessun appuntamento in programma.': 'No upcoming events.',
  'Infostaff recenti': 'Recent infostaff',
  'Tutte →': 'All →',
  'Nessuna nuova comunicazione.': 'No new announcements.',
  'Il mio turno': 'My shift',
  'Vedi settimana →': 'See week →',
  '— nessun turno assegnato —': '— no shift assigned —',
  // shift status codes (compact)
  FERIE: 'LEAVE',
  MALATTIA: 'SICK',
  PERMESSO: 'PERMIT',

  // ── Shifts (Turni) ──
  'Scegli una sede, poi clicca:': 'Pick a location, then click:',
  'metà sinistra': 'left half',
  'tutto il giorno': 'whole day',
  'metà destra': 'right half',
  'solo 2ª parte': 'second part only',
  'Agenda del singolo addetto, giorno per giorno.': 'Individual staff agenda, day by day.',
  'Chi lavora e dove, raggruppato per sede.': 'Who works where, grouped by location.',
  Griglia: 'Grid',
  Persona: 'Person',
  Giornata: 'Day',
  'Griglia per creare i turni': 'Grid to build the roster',
  'Agenda per persona': 'Per-person agenda',
  "Chi c'è, per giornata": "Who's in, by day",
  'sett.': 'wk',
  Inizio: 'Start',
  Fine: 'End',
  'Orario di inizio (1ª metà)': 'Start time (1st half)',
  'Orario di fine (2ª metà)': 'End time (2nd half)',
  Svuota: 'Clear',
  'Copia la selezione': 'Copy selection',
  Copia: 'Copy',
  'Incolla nella selezione': 'Paste into selection',
  Incolla: 'Paste',
  'Svuota le celle selezionate': 'Clear selected cells',
  Cancella: 'Clear',
  Deseleziona: 'Deselect',
  'Tutta la settimana': 'Whole week',
  'Questa giornata per tutti': 'This day for everyone',
  Dipendente: 'Employee',
  'Tutta la settimana di questa persona': "This person's whole week",
  'Sinistra = tutto il giorno · destra = 2ª parte': 'Left = whole day · right = 2nd part',
  'Clic per selezionare · doppio clic per gli orari': 'Click to select · double-click for times',
  'OFF / Riposo': 'OFF / Rest',
  'Assenze approvate:': 'Approved absences:',
  '(“PERM 10:00–13:00” = permesso a ore nel turno)': '(“PERM 10:00–13:00” = hourly time off within the shift)',
  'Attivo:': 'Active:',
  'tocca le celle · tocca nome/giorno per riempire riga/colonna ·': 'tap cells · tap a name/day to fill the row/column ·',
  'per disattivare': 'to turn it off',
  'Nessuno strumento attivo: tocca le celle per selezionare un’area (tocca due angoli), poi usa Copia / Incolla / Cancella':
    'No tool active: tap cells to select an area (tap two corners), then use Copy / Paste / Clear',
  'blocco copiato': 'copied block',
  selezionate: 'selected',
  'Tocca 2 angoli per selezionare · tieni premuto per gli orari': 'Tap 2 corners to select · long-press for times',

  // ── Shifts · contract hours and the auto-generator ──
  Auto: 'Auto',
  'Fabbisogno, preferenze e generazione automatica': 'Staffing needs, preferences and auto-generation',
  'Fabbisogno per sede, preferenze del personale e generazione automatica.':
    'Staffing needs per location, staff preferences and auto-generation.',
  'Oltre il contratto:': 'Over contract:',
  '{n} persone oltre il contratto:': '{n} people over contract:',
  'Ore pianificate nel periodo · contratto {h}h a settimana': 'Hours planned in the period · {h}h/week contract',
  'Ore/sett.:': 'Hours/week:',
  'Ferie/anno (gg):': 'Leave/year (days):',
  Fabbisogno: 'Staffing',
  Preferenze: 'Preferences',
  'Ore e ferie': 'Hours & leave',
  'Quante persone servono': 'How many people are needed',
  'Per ogni sede e giorno della settimana. 0 = sede chiusa o non presidiata.':
    'Per location and weekday. 0 = closed or unstaffed.',
  'Salva fabbisogno': 'Save staffing',
  'Fabbisogno salvato.': 'Staffing saved.',
  'Salvataggio del fabbisogno non riuscito.': 'Could not save the staffing needs.',
  'Nessuna sede configurata.': 'No locations configured.',
  Sede: 'Location',
  Tot: 'Tot',
  'Totale presenze richieste a settimana:': 'Total people needed per week:',
  'Preferenze del personale': 'Staff preferences',
  'Le sedi preferite sono un desiderio, quelle escluse e il “niente weekend” sono vincoli che il generatore non viola mai.':
    'Preferred locations are a wish; excluded ones and “no weekends” are hard limits the generator never breaks.',
  'nessuna preferenza': 'no preferences',
  'no weekend': 'no weekends',
  'Sedi preferite': 'Preferred locations',
  'Da escludere': 'To exclude',
  'Mai weekend e festivi': 'Never weekends or holidays',
  'Max giorni/sett.': 'Max days/week',
  'Preferenze salvate.': 'Preferences saved.',
  'Salvataggio delle preferenze non riuscito.': 'Could not save the preferences.',
  'Ore pianificate e assenze usate': 'Planned hours and leave used',
  Contratto: 'Contract',
  'Ore periodo': 'Period hours',
  'Ferie usate': 'Leave used',
  'Ferie residue': 'Leave left',
  Permessi: 'Time off',
  '/sett.': '/wk',
  gg: 'd',
  'Almeno una settimana supera il contratto': 'At least one week goes over contract',
  'Genera turni': 'Generate roster',
  'Riempie i buchi del periodo mostrato, rispettando contratti, preferenze e assenze approvate. I turni già inseriti restano.':
    'Fills the gaps in the shown period, respecting contracts, preferences and approved leave. Existing shifts are kept.',
  'Calcolo…': 'Computing…',
  Applica: 'Apply',
  'Applicare la proposta?': 'Apply the proposal?',
  'I {n} turni proposti verranno scritti nella griglia. I turni già presenti non vengono toccati.':
    'The {n} proposed shifts will be written to the grid. Existing shifts are left untouched.',
  '{n} turni inseriti.': '{n} shifts added.',
  'Generazione non riuscita.': 'Generation failed.',
  'turni proposti': 'proposed shifts',
  'slot scoperti': 'uncovered slots',
  'per turno': 'per shift',
  Applicata: 'Applied',
  Giorni: 'Days',
  Ore: 'Hours',
  Riposi: 'Rest days',
  'Weekend/festivi': 'Weekends/holidays',
  'giorni di riposo': 'rest days',
  'Fabbisogno non raggiunto: mancano {n} presenze.': 'Staffing not met: {n} people short.',
  'Nessuno era disponibile per questi turni — servono ore, disponibilità o preferenze diverse.':
    'Nobody was available for these shifts — it needs different hours, availability or preferences.',
  'Fabbisogno non coperto in {n} giornate:': 'Staffing not met on {n} days:',
  'Nessuna sede configurata per la pianificazione automatica.': 'No location is set up for automatic planning.',
  'Fuori dal piano automatico:': 'Outside the automatic plan:',
  'Max ore/sett.': 'Max hours/week',
  'Ore massime settimanali di {name}': 'Weekly hour cap for {name}',
  'Le ore massime settimanali sono modificabili qui: il generatore non le supera mai e la griglia avvisa quando vengono sforate. Vuoto = 40h.':
    'The weekly hour cap is editable here: the generator never exceeds it and the grid warns when it is passed. Empty = 40h.',
  'Salvataggio delle ore non riuscito.': 'Could not save the hours.',
  'Solo manuale': 'Manual only',
  'Inclusa nella generazione automatica dei turni': 'Included in the automatic roster generation',
  'Esclusa dalla generazione automatica dei turni': 'Excluded from the automatic roster generation',
  'Una modifica non è stata salvata.': 'One change was not saved.',
  '{n} modifiche non sono state salvate.': '{n} changes were not saved.',
  'Nessun dipendente attivo. Aggiungi utenti dalla Dashboard Admin.': 'No active employees. Add users from the Admin dashboard.',
  'Nessun dipendente attivo.': 'No active employees.',
  'Scegli persona': 'Choose person',
  ' (io)': ' (me)',
  Turno: 'Shift',
  Assenza: 'Absence',
  'Nessun turno assegnato in questa giornata.': 'No shifts assigned on this day.',
  'Riposo & assenze': 'Rest & absences',
  'Senza turno:': 'No shift:',
  'Scegli almeno una sede.': 'Pick at least one location.',
  'Turno (sede)': 'Shift (location)',
  'OFF / Assenza': 'OFF / Absence',
  '1ª parte': '1st part',
  '2ª parte (turno spezzato)': '2nd part (split shift)',
  'Rimuovi 2ª parte': 'Remove 2nd part',
  'Aggiungi 2ª parte (turno spezzato)': 'Add 2nd part (split shift)',
  'Svuota giornata': 'Clear day',
  'Salva turno': 'Save shift',
  '— sede —': '— location —',
  'Orario inizio': 'Start time',

  // ── Calendar ──
  Lun: 'Mon',
  Mar: 'Tue',
  Mer: 'Wed',
  Gio: 'Thu',
  Ven: 'Fri',
  Sab: 'Sat',
  Dom: 'Sun',
  'Mese precedente': 'Previous month',
  'Mese successivo': 'Next month',
  'Nuovo evento': 'New event',
  'Il mio turno: {label}': 'My shift: {label}',
  'Data evento': 'Event date',
  'Turni del giorno': "Day's shifts",
  'Nessun evento in questa giornata.': 'No events on this day.',
  invitato: 'invited',
  'Invitato da {name}': 'Invited by {name}',
  Invitato: 'Invited',
  'Modifica evento': 'Edit event',
  'Modifica {title}': 'Edit {title}',
  'Elimina evento': 'Delete event',
  'Elimina {title}': 'Delete {title}',
  'Eliminare l’evento “{title}”?': 'Delete the event “{title}”?',
  'Titolo evento': 'Event title',
  Luogo: 'Location',
  'Descrizione (opzionale)': 'Description (optional)',
  'Invita persone': 'Invite people',
  'Colore evento': 'Event color',
  'Aggiungi evento': 'Add event',
  'Salva modifiche': 'Save changes',

  // ── Tasks + TaskModal ──
  'Le mie': 'Mine',
  'Per ufficio': 'By office',
  'Per reparto': 'By department',
  'Attività per persona, ufficio o reparto. I task di ufficio li vede chi è in turno lì.':
    'Tasks by person, office or department. Office tasks are seen by whoever is on shift there.',
  'Nuovo task…': 'New task…',
  'Assegna a me': 'Assign to me',
  Ufficio: 'Office',
  "Ufficio (chi è in turno)": "Office (who's on shift)",
  'Senza ricorrenza': 'No recurrence',

  // ── Task composer ──
  'Nuovo task… (es. Apertura cassa)': 'New task… (e.g. Open the till)',
  'Titolo del task': 'Task title',
  Opzioni: 'Options',
  'Creazione…': 'Creating…',
  'Crea {n}': 'Create {n}',
  Per: 'For',
  'A me': 'To me',
  Sedi: 'Locations',
  Chi: 'Who',
  '— scegli —': '— choose —',
  'Una volta': 'Once',
  Ora: 'Time',
  'Ora entro cui va fatto': 'Time it should be done by',
  Bassa: 'Low',
  Media: 'Medium',
  Nessuno: 'None',
  alle: 'at',
  me: 'me',
  'nessun destinatario': 'no recipient',
  'alla spunta si riapre sul giorno successivo': 'ticking it off reopens it on the next day',
  'Task creato.': 'Task created.',
  '{n} task creati.': '{n} tasks created.',
  'Creazione del task non riuscita.': 'Could not create the task.',
  'Giorno precedente': 'Previous day',
  'Giorno successivo': 'Next day',
  'Stai guardando {d}, non oggi.': 'You are looking at {d}, not today.',
  'Ambito dei task': 'Task scope',
  'Stato dei task': 'Task status',
  'Nessun task': 'No tasks',
  'Aggiungi il primo task qui sopra.': 'Add your first task above.',
  'Nessun task in questa vista.': 'No tasks in this view.',
  '{a} in turno · {b} task': '{a} on shift · {b} tasks',
  'Nessun task.': 'No tasks.',
  '{a} membri · {b} task': '{a} members · {b} tasks',
  'Task completata': 'Task completed',
  'Modifica task': 'Edit task',
  'Titolo task': 'Task title',
  Priorità: 'Priority',
  'Assegnata a': 'Assigned to',
  '— nessuno (pool comune) —': '— nobody (shared pool) —',
  '— scegli ufficio —': '— choose office —',
  'Visibile solo a chi è in turno in questo ufficio oggi.': 'Visible only to those on shift in this office today.',
  '— scegli reparto —': '— choose department —',
  Scadenza: 'Due date',
  Rimuovi: 'Remove',
  Ricorrenza: 'Recurrence',
  Etichetta: 'Label',
  'es. Vaticani, Voucher…': 'e.g. Vatican, Voucher…',
  Sottotask: 'Subtasks',
  'Aggiungi sottotask…': 'Add subtask…',
  'Descrizione / note': 'Description / notes',
  'Dettagli, link, riferimenti…': 'Details, links, references…',
  'Eliminare questa task?': 'Delete this task?',
  Nessuna: 'None',
  'Ogni giorno': 'Every day',
  'Lun–Ven': 'Mon–Fri',
  'Ogni lunedì': 'Every Monday',
  'Ogni venerdì': 'Every Friday',

  // ── Handover ──
  'Salvataggio non riuscito': 'Save failed',
  '{n} in turno': '{n} on shift',
  Modifica: 'Edit',
  'Scrivi consegna': 'Write handover',
  'Dal turno precedente': 'From the previous shift',
  'Cosa deve sapere chi arriva domani…': "What should tomorrow's shift know…",
  'Salva consegna': 'Save handover',
  'Consegna di': 'Handover for',
  'Nessuna consegna per questa giornata — scrivila a fine turno.': 'No handover for this day — write it at the end of your shift.',
  'Nessuna consegna per questa giornata.': 'No handover for this day.',
  Storico: 'History',
  'Sedi con personale in turno nella giornata scelta. Storico dell’ultimo mese incluso.':
    'Offices staffed on the selected day. Includes the last month of history.',
  'Le sedi in cui sei in turno. Puoi scrivere la consegna solo per quelle.':
    'The offices where you are on shift. You can only write handovers for those.',
  'Solo rilevanti': 'Relevant only',
  'Tutte le sedi': 'All offices',
  'Nessuna sede': 'No offices',
  'Crea gli uffici dalla Dashboard Admin.': 'Create offices from the Admin dashboard.',
  'Nessuna sede da mostrare': 'No offices to show',
  'Nessuno risulta in turno in questa giornata.': 'Nobody is on shift on this day.',
  'Non risulti in turno in nessuna sede in questa giornata: non puoi scrivere consegne.':
    'You are not on shift at any office on this day: you cannot write handovers.',
  'Mostra comunque tutte le sedi': 'Show all offices anyway',

  // ── Tools ──
  'Modifica tool': 'Edit tool',
  'Nuovo tool': 'New tool',
  'Icona (2 lettere o emoji)': 'Icon (2 letters or emoji)',
  'es. WM oppure 🎫': 'e.g. WM or 🎫',
  Colore: 'Color',
  Nome: 'Name',
  'Nome del tool': 'Tool name',
  Descrizione: 'Description',
  'Breve descrizione': 'Short description',
  Categoria: 'Category',
  'es. Software interno': 'e.g. Internal software',
  'Link / sito web': 'Link / website',
  'Tag (opzionale)': 'Tag (optional)',
  'es. Estensione Chrome': 'e.g. Chrome extension',
  'Caricamento…': 'Loading…',
  'Errore:': 'Error:',
  'Strumenti e collegamenti operativi.': 'Operational tools and links.',
  'Nessuno strumento configurato': 'No tools configured',
  'Aggiungi il primo con “Nuovo tool”.': 'Add the first one with “New tool”.',
  "L'elenco viene popolato dagli amministratori.": 'The list is populated by administrators.',
  '{n} strumento': '{n} tool',
  '{n} strumenti': '{n} tools',
  'Eliminare "{name}"?': 'Delete "{name}"?',

  // ── Company mail / directory ──
  'La Direzione Generale': 'General Management',
  'va inserita in copia nelle comunicazioni rilevanti.': "should be cc'd on relevant communications.",
  'Punti vendita e sedi': 'Stores & offices',
  'Indicazioni operative': 'Operational guidelines',
  Copiato: 'Copied',
  'Senza reparto': 'No department',
  'Carico…': 'Loading…',
  'Nessun membro corrisponde.': 'No members match.',
  'Elenco generato automaticamente da chi è iscritto alla piattaforma, suddiviso per reparto. Usa i pulsanti per copiare gli indirizzi e incollarli nel campo destinatari della mail.':
    'List generated automatically from platform members, grouped by department. Use the buttons to copy addresses and paste them into the email recipients field.',
  'Copia tutti ({n})': 'Copy all ({n})',
  'Copia reparto ({n})': 'Copy department ({n})',
  Sezione: 'Section',
  '+ Nuova sezione…': '+ New section…',
  'Nome della nuova sezione': 'New section name',
  'Nome / casella': 'Name / mailbox',
  'Es. Prenotazioni Roma': 'e.g. Bookings Rome',
  'Ruolo (opz.)': 'Role (opt.)',
  Responsabile: 'Manager',
  'Telefono (opz.)': 'Phone (opt.)',
  'Note (opz.)': 'Notes (opt.)',
  'Quando usarla, chi la presidia…': 'When to use it, who manages it…',
  'Sezione, nome ed email sono obbligatori.': 'Section, name and email are required.',
  'Salvataggio…': 'Saving…',
  'Creazione non riuscita.': 'Creation failed.',
  'Modifica non riuscita.': 'Update failed.',
  'Eliminazione non riuscita.': 'Deletion failed.',
  'Eliminare il contatto “{name}”?': 'Delete the contact “{name}”?',
  'Nuovo contatto': 'New contact',
  'Aggiungi contatto': 'Add contact',
  'Rubrica vuota': 'Empty directory',
  'Aggiungi il primo contatto.': 'Add the first contact.',
  'I contatti aziendali vengono gestiti dagli amministratori.': 'Company contacts are managed by administrators.',
  'Rubrica aziendale': 'Company directory',
  'Membri per reparto': 'Members by department',
  Contatti: 'Contacts',
  'Riferimenti aziendali, rubrica del personale e contatti.': 'Company references, staff directory and contacts.',
  'Cerca persona…': 'Search person…',

  // ── Profile ──
  'Le due nuove password non coincidono.': 'The two new passwords do not match.',
  'Password aggiornata.': 'Password updated.',
  'Cambia password': 'Change password',
  'Minimo 8 caratteri. Le altre sessioni verranno disconnesse.': 'Minimum 8 characters. Other sessions will be logged out.',
  'Password attuale': 'Current password',
  'Nuova password': 'New password',
  'Conferma nuova password': 'Confirm new password',
  'Almeno 8 caratteri.': 'At least 8 characters.',
  'Aggiorno…': 'Updating…',
  'Aggiorna password': 'Update password',
  Amministratore: 'Administrator',
  Utente: 'User',
  'Immagine aggiornata': 'Image updated',
  'Caricamento non riuscito': 'Upload failed',
  'Immagine rimossa': 'Image removed',
  'Nickname salvato': 'Nickname saved',
  'Cambia immagine': 'Change image',
  'Cambia immagine profilo': 'Change profile picture',
  'Cambia foto': 'Change photo',
  'Come vuoi essere chiamato': 'How you want to be called',
  'Il nickname è': 'Your nickname is',
  facoltativo: 'optional',
  'e compare solo nel tuo Hub (“Buongiorno, …”) al posto del nome. Nessun altro lo vede.':
    'and appears only in your Hub (“Good morning, …”) instead of your name. Nobody else sees it.',
  'Salvo…': 'Saving…',
  'Anteprima: “Buongiorno, {nick}.”': 'Preview: “Good morning, {nick}.”',
  'Lascia vuoto per usare il tuo nome.': 'Leave empty to use your name.',
  'Account interno Touristation.': 'Internal Touristation account.',
  Esci: 'Log out',

  // ── Settings ──
  errore: 'error',
  'Logo aziendale': 'Company logo',
  'Carica logo': 'Upload logo',
  'Ripristina default': 'Restore default',
  'PNG o SVG con sfondo trasparente, quadrato. Viene mostrato nella sidebar e nel login.':
    'PNG or SVG with transparent background, square. Shown in the sidebar and on the login screen.',
  'Il nickname compare solo nel tuo Hub (“Buongiorno, …”). Nessun altro utente lo vede.':
    'The nickname appears only in your Hub (“Good morning, …”). No other user sees it.',
  'Il tuo nome': 'Your name',
  'Salvato.': 'Saved.',
  Aspetto: 'Appearance',
  Tema: 'Theme',
  Chiaro: 'Light',
  Scuro: 'Dark',

  // ── Notes ──
  Giallo: 'Yellow',
  Verde: 'Green',
  Azzurro: 'Blue',
  Rosa: 'Pink',
  Viola: 'Purple',
  Corallo: 'Coral',
  Grigio: 'Gray',
  'Senza titolo': 'Untitled',
  'Tabella {r}×{c}': 'Table {r}×{c}',
  '1 immagine': '1 image',
  '{n} immagini': '{n} images',
  '{n} video': '{n} videos',
  '{n} attività': '{n} tasks',
  'Apri {title}': 'Open {title}',
  'nota senza titolo': 'untitled note',
  'Nota vuota': 'Empty note',
  'Aggiornata {date}': 'Updated {date}',
  'Rimuovi dai preferiti': 'Remove from favorites',
  'Aggiungi ai preferiti': 'Add to favorites',
  'Nome cartella': 'Folder name',
  'Eliminare questa nota?': 'Delete this note?',
  'Eliminazione non riuscita': 'Deletion failed',
  'Salvataggio finale…': 'Final save…',
  'Salvataggio non riuscito · nuovo tentativo': 'Save failed · retrying',
  'Modifiche in attesa': 'Pending changes',
  'Salvato alle {time}': 'Saved at {time}',
  'Salvato · {time}': 'Saved · {time}',
  'Titolo della nota': 'Note title',
  'Opzioni nota': 'Note options',
  'Salva e chiudi': 'Save and close',
  'Scrivi i tuoi appunti… (grassetto, corsivo, link, immagini)': 'Write your notes… (bold, italic, links, images)',
  'Nessun colore': 'No color',
  'Colore {c}': 'Color {c}',
  Cartella: 'Folder',
  'Cartella della nota': "Note's folder",
  'Nessuna cartella': 'No folder',
  'Nuova sotto-cartella': 'New sub-folder',
  Rinomina: 'Rename',
  'Elimina cartella': 'Delete folder',
  'Eliminare “{name}” e le sue sotto-cartelle? Le note resteranno.': 'Delete “{name}” and its sub-folders? Notes will remain.',
  'Note private. Trascina le note nelle cartelle, crea sotto-cartelle e formatta a piacere.':
    'Private notes. Drag notes into folders, create sub-folders and format freely.',
  'Cerca…': 'Search…',
  'Nuova nota': 'New note',
  Preferiti: 'Favorites',
  'Senza cartella': 'No folder',
  'Nessuna cartella.': 'No folders.',
  'Nuova cartella': 'New folder',
  'Nessun appunto': 'No notes',
  'Crea la tua prima nota privata con “Nuova nota”.': 'Create your first private note with “New note”.',
  'Nessuna nota per “{q}”.': 'No notes for “{q}”.',
  'Nessuna nota qui. Trascina una nota o creane una nuova.': 'No notes here. Drag a note or create a new one.',
  'Rinomina cartella': 'Rename folder',

  // ── Learning / Training ──
  '{n} lezioni': '{n} lessons',
  Obbligatorio: 'Mandatory',
  Bozza: 'Draft',
  'Scaduto il ': 'Overdue on ',
  'Entro il ': 'By ',
  Rivedi: 'Review',
  Riprendi: 'Resume',
  Inizia: 'Start',
  Iscrivimi: 'Enrol me',
  Anteprima: 'Preview',
  Assegna: 'Assign',
  Statistiche: 'Statistics',
  'Carico le statistiche…': 'Loading statistics…',
  'Statistiche non disponibili': 'Statistics unavailable',
  Indietro: 'Back',
  Assegnazioni: 'Assignments',
  Completati: 'Completed',
  'In corso': 'In progress',
  'Non iniziati': 'Not started',
  '{n}% del totale': '{n}% of total',
  Stato: 'Status',
  Avanzamento: 'Progress',
  'Completato il': 'Completed on',
  Completato: 'Completed',
  'Non iniziato': 'Not started',
  'Rimuovi assegnazione': 'Remove assignment',
  'Rimuovere l’assegnazione a {name}?': 'Remove the assignment for {name}?',
  'Nessuno ha ancora questo corso assegnato.': 'Nobody has this course assigned yet.',
  'Obbligatori scaduti': 'Overdue mandatory',
  '{n} persone': '{n} people',
  'Chi non ha completato un corso obbligatorio oltre la scadenza.': 'Who has not completed a mandatory course past the deadline.',
  'Nessuno in ritardo. Tutto in regola.': 'Nobody overdue. All good.',
  '{n}g in ritardo': '{n}d overdue',
  'Iscrizione non riuscita': 'Enrolment failed',
  'I miei percorsi': 'My courses',
  Percorsi: 'Courses',
  Catalogo: 'Catalog',
  'Gestione & invio': 'Manage & send',
  Gestione: 'Manage',
  'Percorsi aziendali: articoli, video, documenti, link e quiz.': 'Company courses: articles, videos, documents, links and quizzes.',
  'Nuovo corso': 'New course',
  'Invia formazione': 'Send training',
  'Sezioni della formazione': 'Training sections',
  Assegnati: 'Assigned',
  'Nessun corso assegnato': 'No courses assigned',
  'Guarda il catalogo: puoi iscriverti da solo ai corsi aperti.': 'Check the catalog: you can self-enrol in open courses.',
  'Vai al catalogo': 'Go to catalog',
  'Cerca un corso…': 'Search a course…',
  '{n} corsi': '{n} courses',
  'Nessun corso': 'No courses',
  'Crea il primo corso con “Nuovo corso”.': 'Create the first course with “New course”.',
  'Non ci sono ancora corsi pubblicati.': 'No published courses yet.',
  Corso: 'Course',
  Lezioni: 'Lessons',
  Accesso: 'Access',
  obbligatorio: 'mandatory',
  Pubblicato: 'Published',
  'Aperto a tutti': 'Open to all',
  'Solo su assegnazione': 'Assignment only',
  'Nessun corso creato.': 'No courses created.',
  '{n} pubblicati': '{n} published',
  Completamento: 'Completion',
  '{n} completati': '{n} completed',
  Obbligatori: 'Mandatory',
  'Corsi per completamento': 'Courses by completion',
  'Nessun corso da mostrare.': 'No courses to show.',
  'Eliminare “{title}”? Anche i progressi di tutti andranno persi.': "Delete “{title}”? Everyone's progress will also be lost.",
  'Corso eliminato': 'Course deleted',
  'questo corso': 'this course',

  // ── Admin dashboard ──
  'Disattivare {name}? Non potrà più accedere.': 'Deactivate {name}? They will no longer be able to log in.',
  'Non puoi disattivare il tuo account': 'You cannot deactivate your own account',
  'Disattiva account': 'Deactivate account',
  'Riattiva account': 'Reactivate account',
  Attivo: 'Active',
  Disattivato: 'Deactivated',
  'Reparto:': 'Department:',
  '— nessuno —': '— none —',
  'Eliminare il ruolo "{name}"?': 'Delete the role "{name}"?',
  'Elimina ruolo': 'Delete role',
  'Accesso admin': 'Admin access',
  'Pubblica infostaff': 'Publish infostaff',
  'Tutti i ticket': 'All tickets',
  'Gestione vault': 'Vault management',
  'Gestione utenti': 'User management',
  'Gestione ruoli': 'Role management',
  'Nome reparto': 'Department name',
  Sigla: 'Short code',
  '{n} membri': '{n} members',
  'Eliminare il reparto "{name}"? I membri verranno scollegati.': 'Delete the department "{name}"? Members will be unlinked.',
  'Nome sede (es. Musei Vaticani)': 'Office name (e.g. Vatican Museums)',
  'Eliminare la sede "{label}"?': 'Delete the office "{label}"?',
  'Utenti, ruoli, reparti e attività registrate.': 'Users, roles, departments and recorded activity.',
  'Utenti totali': 'Total users',
  '{n} attivi': '{n} active',
  'Infostaff pubblicate': 'Published infostaff',
  'comunicazioni interne': 'internal announcements',
  'Task aperti': 'Open tasks',
  'Ruoli configurati': 'Configured roles',
  '{n} reparti': '{n} departments',
  'Cerca utente…': 'Search user…',
  'Nuovo utente': 'New user',
  'Nome e cognome': 'Full name',
  'Email aziendale': 'Company email',
  'Ruoli:': 'Roles:',
  'Crea utente': 'Create user',
  'Nessun utente': 'No users',
  '{n} utenti': '{n} users',
  'Ruoli & permessi': 'Roles & permissions',
  'Nuovo ruolo': 'New role',
  'Nome del ruolo (es. Supervisor)': 'Role name (e.g. Supervisor)',
  Crea: 'Create',
  'Dopo la creazione, attiva i permessi cliccando sulle etichette nella scheda del ruolo.': 'After creating it, enable permissions by clicking the labels on the role card.',
  'Nessun ruolo': 'No roles',
  Reparti: 'Departments',
  'Nuovo reparto': 'New department',
  'Nessun reparto': 'No departments',
  'Uffici e sedi': 'Offices & locations',
  'Usati nei Turni e per raggruppare i todo per sede.': 'Used in Shifts and to group to-dos by location.',
  'Nuova sede': 'New office',
  'Sigla (es. VAT)': 'Short code (e.g. VAT)',
  'Aggiungi ufficio, point of sale o smart working.': 'Add an office, point of sale or remote work.',
  'Nessun evento registrato': 'No recorded events',
  Quando: 'When',
  Attore: 'Actor',
  Azione: 'Action',
  Risorsa: 'Resource',

  // ── Wiki ──
  'Primo livello': 'Top level',
  Comprimi: 'Collapse',
  Espandi: 'Expand',
  'Nuova sotto-pagina': 'New sub-page',
  'Tutte le pagine': 'All pages',
  'Trascina per riordinare o annidare': 'Drag to reorder or nest',
  'Sposta al primo livello': 'Move to top level',
  'Sezioni Wiki': 'Wiki sections',
  Pagine: 'Pages',
  'Torna alla cartella precedente': 'Back to previous folder',
  '{n} elementi': '{n} items',
  '{n} risultati': '{n} results',
  'Nessun risultato.': 'No results.',
  'Nessuna pagina preferita.': 'No favorite pages.',
  'Questa cartella è vuota.': 'This folder is empty.',
  'Agg. {date}': 'Upd. {date}',
  'Apri la pagina {title}': 'Open page {title}',
  'Nessuna pagina.': 'No pages.',
  'Cronologia versioni': 'Version history',
  'Nessuna versione registrata.': 'No versions recorded.',
  'Nuova pagina': 'New page',
  Aggiornamento: 'Update',
  'Wiki vuota': 'Empty wiki',
  'Crea la prima pagina della knowledge base.': 'Create the first page of the knowledge base.',
  'Pagine Wiki': 'Wiki pages',
  '{n} pagine e cartelle': '{n} pages and folders',
  'Chiudi pagine Wiki': 'Close Wiki pages',
  'Cerca pagine e tag': 'Search pages and tags',
  'Cerca pagine, tag…': 'Search pages, tags…',
  'Cancella ricerca': 'Clear search',
  'Vai a…': 'Go to…',
  'Esplora le pagine': 'Browse pages',
  'Azioni pagina': 'Page actions',
  Preferito: 'Favorite',
  'Cronologia, versione {v}': 'History, version {v}',
  'Cronologia · ': 'History · ',
  'Vai a': 'Go to',
  'Salva versione': 'Save version',
  'Aggiornata da': 'Updated by',
  'Responsabile:': 'Owner:',
  '{w} parole · ~{m} min': '{w} words · ~{m} min',
  'Questa è una cartella. Le pagine al suo interno sono elencate qui sotto.': 'This is a folder. Its pages are listed below.',
  'Pagine ({n})': 'Pages ({n})',
  'agg. {date}': 'upd. {date}',
  'Collegamenti in entrata': 'Incoming links',
  'In questa pagina': 'On this page',
  'Emoji icona': 'Emoji icon',
  'Titolo pagina': 'Page title',
  'Tag separati da virgola': 'Comma-separated tags',
  'Responsabile: nessuno': 'Owner: none',
  'Responsabile: {name}': 'Owner: {name}',
  'Inizia a scrivere…': 'Start writing…',
  'Vai a una pagina…': 'Go to a page…',

  // ── Rich text editor ──
  Taglia: 'Cut',
  'Formato testo': 'Text format',
  Grassetto: 'Bold',
  Corsivo: 'Italic',
  Sottolineato: 'Underline',
  Barrato: 'Strikethrough',
  'Codice inline': 'Inline code',
  Blocco: 'Block',
  'Titolo 1': 'Heading 1',
  'Titolo 2': 'Heading 2',
  'Titolo 3': 'Heading 3',
  Citazione: 'Quote',
  'Elenco puntato': 'Bulleted list',
  'Elenco numerato': 'Numbered list',
  Inserisci: 'Insert',
  'Link…': 'Link…',
  'Immagine…': 'Image…',
  'Tabella…': 'Table…',
  Separatore: 'Divider',
  'Formattazione testo': 'Text formatting',
  'Tipo di blocco': 'Block type',
  Paragrafo: 'Paragraph',
  Codice: 'Code',
  'Grassetto (Ctrl+B)': 'Bold (Ctrl+B)',
  'Corsivo (Ctrl+I)': 'Italic (Ctrl+I)',
  'Sottolineato (Ctrl+U)': 'Underline (Ctrl+U)',
  'Altri strumenti': 'More tools',
  'Da fare': 'To do',
  Immagine: 'Image',
  Tabella: 'Table',
  'Rimuovi formattazione': 'Clear formatting',
  'Mostra il Markdown': 'Show Markdown source',
  'Torna all’editor visuale': 'Back to the visual editor',
  'Sorgente Markdown': 'Markdown source',
  Contenuto: 'Content',
  'Inserisci link': 'Insert link',
  'Pagina wiki': 'Wiki page',
  'Link esterno': 'External link',
  'Digita per filtrare…': 'Type to filter…',
  'Testo personalizzato (opzionale)': 'Custom text (optional)',
  'Etichetta visibile (opzionale)': 'Visible label (optional)',
  'Inserisci immagine': 'Insert image',
  'Da URL': 'From URL',
  'Carica file': 'Upload file',
  'Descrizione immagine': 'Image description',
  'Inserisci video': 'Insert video',
  'Inserisci tabella': 'Insert table',
  Righe: 'Rows',
  Colonne: 'Columns',
  Intestazione: 'Header',
  Colonna: 'Column',

  // ── Learning · lesson types / assign / player ──
  Articolo: 'Article',
  'Documento (PDF)': 'Document (PDF)',
  'Scegli un corso.': 'Choose a course.',
  'Seleziona almeno un destinatario.': 'Select at least one recipient.',
  'Corso assegnato a {n} persona': 'Course assigned to {n} person',
  'Corso assegnato a {n} persone': 'Course assigned to {n} people',
  'Assegnazione non riuscita.': 'Assignment failed.',
  '— scegli un corso —': '— choose a course —',
  ' (bozza)': ' (draft)',
  Destinatari: 'Recipients',
  Persone: 'People',
  'Tutta l’azienda': 'Whole company',
  'Nessun reparto configurato.': 'No departments configured.',
  'Il corso sarà assegnato a tutti i {n} utenti attivi.': 'The course will be assigned to all {n} active users.',
  'Scadenza (opz.)': 'Due date (opt.)',
  '{n} destinatario': '{n} recipient',
  '{n} destinatari': '{n} recipients',
  'Invio…': 'Sending…',
  'Nessun video caricato.': 'No video uploaded.',
  'Nessun documento caricato.': 'No document uploaded.',
  'Questa lezione si svolge su una risorsa esterna.': 'This lesson takes place on an external resource.',
  'Apri la risorsa': 'Open the resource',
  'Questo quiz non ha ancora domande.': 'This quiz has no questions yet.',
  'Superato con {score}% (minimo {min}%).': 'Passed with {score}% (minimum {min}%).',
  'Non superato: {score}%, serve almeno {min}%. Puoi riprovare.': 'Not passed: {score}%, at least {min}% required. You can retry.',
  'Quiz già superato.': 'Quiz already passed.',
  '{n} risposte pronte': '{n} answers ready',
  'Rispondi a tutte le domande per inviare.': 'Answer all questions to submit.',
  Riprova: 'Retry',
  'Invia risposte': 'Submit answers',
  'Carico il corso…': 'Loading the course…',
  'Corso non trovato': 'Course not found',
  'Torna indietro': 'Go back',
  'Corso completato!': 'Course completed!',
  'Operazione non riuscita': 'Operation failed',
  'Non sei ancora iscritto a questo corso.': 'You are not enrolled in this course yet.',
  'Iscriviti per registrare i tuoi progressi.': 'Enrol to track your progress.',
  'Scadenza:': 'Due date:',
  'Nessuna lezione': 'No lessons',
  'Questo corso non ha ancora contenuti.': 'This course has no content yet.',
  'Prec.': 'Prev.',
  'Succ.': 'Next',
  'Quiz superato ({score}%)': 'Quiz passed ({score}%)',
  'Quiz non superato ({score}%)': 'Quiz not passed ({score}%)',
  'Lezione {i} di {n}': 'Lesson {i} of {n}',
  'Segna come completata': 'Mark as completed',

  // ── Learning · course builder ──
  'Soglia di superamento': 'Passing threshold',
  '% di risposte corrette': '% of correct answers',
  'Percentuale di risposte esatte necessaria per superare il quiz. Sotto questa soglia la lezione resta “da completare” e la persona può riprovare.':
    'Percentage of correct answers needed to pass the quiz. Below this threshold the lesson stays "to complete" and the person can retry.',
  'Domanda {n}': 'Question {n}',
  'Aggiungi immagine alla domanda': 'Add image to question',
  'Elimina domanda': 'Delete question',
  'Rimuovi immagine': 'Remove image',
  'Risposta corretta': 'Correct answer',
  'Opzione {n}': 'Option {n}',
  'Rimuovi opzione': 'Remove option',
  Opzione: 'Option',
  'Seleziona il pallino della risposta corretta.': 'Select the radio of the correct answer.',
  'Aggiungi domanda': 'Add question',
  'Titolo della lezione': 'Lesson title',
  'Durata (min)': 'Duration (min)',
  Su: 'Up',
  Giù: 'Down',
  'Elimina lezione': 'Delete lesson',
  'Scrivi il contenuto della lezione…': 'Write the lesson content…',
  'File video': 'Video file',
  'File PDF': 'PDF file',
  '…oppure incolla un URL': '…or paste a URL',
  'URL della risorsa': 'Resource URL',
  'Il titolo è obbligatorio.': 'The title is required.',
  'Ogni lezione deve avere un titolo.': 'Each lesson must have a title.',
  'La lezione “{title}” non ha un URL.': 'The lesson “{title}” has no URL.',
  'Il quiz “{title}” non ha domande.': 'The quiz “{title}” has no questions.',
  'Una domanda del quiz “{title}” è vuota.': 'A question in the quiz “{title}” is empty.',
  'Il quiz “{title}” ha opzioni vuote.': 'The quiz “{title}” has empty options.',
  'Un corso pubblicato deve avere almeno una lezione.': 'A published course must have at least one lesson.',
  'Corso aggiornato': 'Course updated',
  'Corso creato': 'Course created',
  'Salvataggio non riuscito.': 'Save failed.',
  'Modifica corso': 'Edit course',
  'Salva corso': 'Save course',
  Dettagli: 'Details',
  Titolo: 'Title',
  'Es. Onboarding nuovo assunto': 'e.g. New hire onboarding',
  'Compliance, Strumenti…': 'Compliance, Tools…',
  'A cosa serve questo corso': 'What this course is for',
  Icona: 'Icon',
  'Corso obbligatorio': 'Mandatory course',
  'Chiunque può iscriversi da solo': 'Anyone can self-enrol',
  'Pubblicato (visibile a tutti)': 'Published (visible to everyone)',
  'Soglia quiz predefinita': 'Default quiz threshold',
  'Usata dai quiz che non ne impostano una propria. È la % di risposte corrette per superare il quiz.':
    'Used by quizzes that do not set their own. It is the % of correct answers to pass the quiz.',
  '{n} lezioni · durata stimata {m} min': '{n} lessons · estimated {m} min',
  'Aggiungi almeno una lezione scegliendo un tipo qui sopra.': 'Add at least one lesson by choosing a type above.',
  'Tipi usati:': 'Types used:',

  // ── Login ──
  'Email o password non validi.': 'Invalid email or password.',
  'Accesso non riuscito. Riprova.': 'Login failed. Please try again.',
  'Workspace aziendale': 'Company workspace',
  'Accesso aziendale': 'Company access',
  'Accedi a Touristation Hub': 'Sign in to Touristation Hub',
  'Turni, task, comunicazioni e documenti aziendali in un unico spazio.': 'Shifts, tasks, announcements and company documents in one place.',
  'Aree principali dell’Hub': 'Main Hub areas',
  'Turni e calendario': 'Shifts and calendar',
  'Infostaff e consegne': 'Infostaff and handovers',
  'Wiki e formazione': 'Wiki and training',
  'Inserisci la password': 'Enter your password',
  'Nascondi password': 'Hide password',
  'Mostra password': 'Show password',
  'Accesso in corso': 'Signing in',
  Accedi: 'Sign in',
  'Accesso riservato al team Touristation.': 'Access restricted to the Touristation team.',
  'Ticketstation S.r.l. · Sistema interno': 'Ticketstation S.r.l. · Internal system',
  'Riprendi animazione': 'Resume animation',
  'Metti in pausa animazione': 'Pause animation',
  'Roma si apre da qui.': 'Rome opens from here.',
  'Biglietti, visite e tour per le grandi icone di Roma e Pompei, coordinati in un unico Hub.':
    'Tickets, visits and tours for the great icons of Rome and Pompeii, coordinated in a single Hub.',
  'Workspace interno Touristation': 'Touristation internal workspace',

  // ── OAuth authorize / consent ──
  'Verificare la tua identità': 'Verify your identity',
  'Il tuo indirizzo email': 'Your email address',
  'I tuoi ruoli aziendali': 'Your company roles',
  'Restare connesso anche quando chiudi la pagina': 'Stay signed in even when you close the page',
  'Richiesta di autorizzazione non valida.': 'Invalid authorization request.',
  'Richiesta non valida': 'Invalid request',
  'Mancano dei parametri obbligatori (client_id, redirect_uri, code_challenge).': 'Required parameters are missing (client_id, redirect_uri, code_challenge).',
  Applicazione: 'Application',
  'Autorizzazione in corso…': 'Authorizing…',
  'Questa applicazione': 'This application',
  'vuole accedere al tuo account Touristation.': 'wants to access your Touristation account.',
  'Accederai come': 'You will sign in as',
  Consenti: 'Allow',

  // ── OAuth clients (Admin) ──
  'App registrata — salva queste credenziali ora': 'App registered — save these credentials now',
  'Il client secret viene mostrato': 'The client secret is shown',
  'una sola volta': 'only once',
  'Conservalo in un posto sicuro.': 'Keep it in a safe place.',
  'App pubblica (solo PKCE): nessun secret.': 'Public app (PKCE only): no secret.',
  'Nome e almeno una redirect URI sono obbligatori.': 'Name and at least one redirect URI are required.',
  'Rigenerare il secret di “{name}”? Il valore attuale smetterà di funzionare.': 'Regenerate the secret for “{name}”? The current value will stop working.',
  'App collegate · Single Sign-On': 'Connected apps · Single Sign-On',
  'App che possono far accedere le persone con l’account Touristation (“Accedi con Touristation”).':
    'Apps that can let people sign in with their Touristation account (“Sign in with Touristation”).',
  'Registra app': 'Register app',
  'Nome app': 'App name',
  'Es. Portale prenotazioni': 'e.g. Booking portal',
  'Redirect URI (una per riga)': 'Redirect URI (one per line)',
  'Deve coincidere esattamente con l’URL a cui l’app riceve il codice.': 'It must exactly match the URL where the app receives the code.',
  'Dati condivisi (scope)': 'Shared data (scope)',
  'App con backend (client secret)': 'App with backend (client secret)',
  'App interna (salta la schermata di consenso)': 'Internal app (skips the consent screen)',
  Registra: 'Register',
  'Nessuna app collegata': 'No connected apps',
  'Registra la prima app che userà il login Touristation.': 'Register the first app that will use Touristation login.',
  interna: 'internal',
  'con secret': 'with secret',
  'Rigenera secret': 'Regenerate secret',
  'Eliminare l’app “{name}”? Smetterà di poter far accedere gli utenti.': 'Delete the app “{name}”? It will stop being able to sign users in.',
};
